import { MultiFactorResolver } from 'firebase/auth';
import { Roles, SignInProvider } from 'firebase/interfaces';
import { createContext } from 'react';
import { useContext } from 'react';
import { AccountInfo } from './AuthProvider';
import {
  GetStripeSubscriptionsQuery,
  Stripe_Billing_Plans,
  Stripe_Subscriptions,
} from 'graphql/generated/hasura';
import { ApolloQueryResult } from '@apollo/client';

export type BillingPlanFromQuery = Omit<
  Stripe_Billing_Plans,
  '__typename' | 'active' | 'stripe_price_id' | 'stripe_product_id'
>;

export type SubscriptionFromQuery = Omit<
  Stripe_Subscriptions,
  | '__typename'
  | 'created_at'
  | 'current_period_start'
  | 'stripe_customer'
  | 'stripe_subscription_id'
  | 'updated_at'
> & {
  stripe_billing_plan: BillingPlanFromQuery;
};

export interface UserData {
  email: string;
  displayName?: string;
  uuid?: string;
  phoneNumber?: string;
  mfaPhoneNumber?: string;
  country?: string;
  state?: string;
  signInProvider: SignInProvider;
  profilePicture?: string;
  role?: Roles;
  providerEnabled?: boolean;
  providerId?: string;
  parentId?: string | null;
  permissions?: string[] | null;
  account_closed_at?: string | null;
  blocked_at?: string | null;
  deletedByEmail?: string | null;
}

export interface AuthContextType {
  isLoggedIn: boolean;
  user: UserData | null;
  mfaResolver: MultiFactorResolver | null;
  isAuthResolved: boolean;
  profilePicture: string | null;
  setUserUuid: (uuid: string) => void;
  setProviderEnabled: (status: boolean) => void;
  setPhoneNumber: (phoneNumber: string) => void;
  setMFAPhoneNumber: (PhoneNumber: string) => void;
  setAvatarUrl: (url: string) => void;
  setCountry: (country: string) => void;
  setMfaResolver: (resolver: MultiFactorResolver | null) => void;
  setProfilePicture: (resolver: string | null) => void;
  setRole: (role: Roles) => void;
  accounts: AccountInfo[];
  refetchSubPatients: () => void;
  login: (
    redirect?: string,
    shouldLoginBeforeRedirect?: boolean,
    shouldSkipRedirectBlock?: boolean,
  ) => void;
  loginWithOutMFA: (redirect: string) => void;
  logout: () => Promise<void>;
  setDisplayName?: (name: string) => void;
  loadingUserFullInfo: boolean;
  handleSelectAccount?: (accountId: string | null) => void;
  selectedAccount?: string | null;
  mainAccountInfo?: AccountInfo | null;
  stripeSubscription?: SubscriptionFromQuery[] | null;
  hasActiveSubscription: boolean;
  hasActivePeriod?: boolean;
  handleReloadUserData?: () => void;
  refetchSubscriptions?: () => Promise<
    ApolloQueryResult<GetStripeSubscriptionsQuery>
  >;
  handleReloadDataAfterDeleteAccount?: (id: string) => void;
  avatarMap?: Map<string, string>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined && process.env.REACT_APP_IS_DEBUG_MODE) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  isAuthResolved: false,
} as AuthContextType);
